import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, signal, OnInit, OnDestroy, Input, Output, EventEmitter, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ResumeDataService } from '../../services/resume-data.service';
import { QuickbooksService } from '../../../../../services/quickbooks/quickbooks.service';
import { DialogsService } from '../../../../../services/dialogs.service';
import { SharedModule } from '../../../../../layout/shared/shared.module';
import { KiteModule } from '../../../../../shared/widgets/kite/kite.module';
import { WidgetsModule } from '../../../../../shared/widgets.module';
import {
  QbMatchCandidate,
  QbTransaction,
  QbFinancialData,
  QbLinkStatus,
  QbDocumentDetail
} from '../../../../../models/quickbooks.models';

// Tier 1: wj-flex-grid replaced by app-kite-grid (wrapper imported via WidgetsModule).
// WjFlexGrid / WjGridModule imports removed.
import {
  createActionColumn,
  createCurrencyColumn,
  createDateColumn,
  createPlainColumn
} from '../../../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
  selector: 'app-qb-financial-tab',
  imports: [CommonModule, SharedModule, KiteModule, WidgetsModule
    // WjGridModule removed (Tier 1.4).
    // KiteGridComponent is reachable via WidgetsModule's exports.
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './qb-financial-tab.component.html',
  styleUrls: ['./qb-financial-tab.component.scss']
})
export class QbFinancialTabComponent implements OnInit, OnDestroy {
  @Input() isActive = false;
  @Output() qbDataChanged = new EventEmitter<void>();
  @ViewChild('linkModal') linkModalTemplate!: TemplateRef<any>;
  @ViewChild('docDetailModal') docDetailModalTemplate!: TemplateRef<any>;

  private readonly resumeData = inject(ResumeDataService);
  private readonly qbService = inject(QuickbooksService);
  private readonly toastr = inject(ToastrService);
  private readonly modalsService = inject(DialogsService);
  private readonly modalService = inject(NgbModal);
  private readonly cdr = inject(ChangeDetectorRef);

  private linkModalRef!: NgbModalRef;
  private messageListener!: (event: MessageEvent) => void;

  // Estado de conexión QB
  readonly qbConnected = signal(false);
  readonly qbRealmId = signal<string | null>(null);
  readonly qbNeedsReauth = signal(false);

  // Estado del link
  readonly isLinked = signal(false);
  readonly linkData = signal<QbLinkStatus['link'] | null>(null);

  // Datos del customer QB vinculado
  readonly qbCustomerName = signal('');

  // Estado financiero
  readonly financialData = signal<QbFinancialData['data'] | null>(null);
  readonly transactions = signal<QbTransaction[]>([]);

  // Matching (modal)
  readonly candidates = signal<QbMatchCandidate[]>([]);

  // Loading states
  readonly loadingStatus = signal(true);
  readonly loadingCandidates = signal(false);
  readonly loadingSyncData = signal(false);
  readonly loadingUnlink = signal(false);
  readonly loadingDocDetail = signal(false);

  // Dirección del cliente actual (para mostrar en el modal)
  readonly clientAddress = signal('');

  // Modal de detalle de documento QB
  readonly selectedDoc = signal<QbDocumentDetail | null>(null);
  private docDetailModalRef!: NgbModalRef;

  // Tier 1: AG Grid column definitions built once at startup.
  txnColumnDefs: any[] = [];
  lineItemsColumnDefs: any[] = [];

  ngOnInit(): void {
    this.checkConnectionAndLink();
    this.buildColumnDefs();

    // Listen for QB sync requests from other widgets (e.g. after reconciliation payment)
    // so we reload the financial data from the updated cache
    this.resumeData.qbSyncRequested$.subscribe(() => {
      this.loadFinancialData();
    });

    // Escuchar postMessage del popup de OAuth
    this.messageListener = (event: MessageEvent) => {
      if (event.data?.type === 'QB_AUTH_SUCCESS') {
        this.toastr.success('QuickBooks connected successfully!');
        this.qbConnected.set(true);
        this.qbRealmId.set(event.data.realmId);
        this.cdr.markForCheck();
        // Abrir el modal de búsqueda automáticamente
        setTimeout(() => this.openSearchModal(), 300);
      } else if (event.data?.type === 'QB_AUTH_ERROR') {
        this.toastr.error('QuickBooks authorization failed. Please try again.');
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
  }

  /** Builds both grids' AG Grid column definitions. Mirrors the legacy
   *  wj-flex-grid-column declarations that this tab used. */
  private buildColumnDefs(): void {
    this.txnColumnDefs = [
      createActionColumn({
        width: 55,
        buttons: [
          {
            icon: 'fa-eye',
            title: 'View document',
            ariaLabel: 'View document',
            classes: 'view-doc-btn'
          }
        ]
      }),
      createPlainColumn({
        field: 'type',
        headerName: 'Type',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'docNumber',
        headerName: 'Doc #',
        flex: 1,
        cellClass: 'ag-cell-center'
      }),
      createDateColumn({
        field: 'date',
        headerName: 'Date',
        flex: 1,
        cellClass: 'ag-cell-center',
        format: 'MM/dd/yyyy'
      }),
      createCurrencyColumn({
        field: 'totalAmount',
        headerName: 'Amount',
        flex: 1,
        cellClass: 'ag-cell-right'
      }),
      createCurrencyColumn({
        field: 'balance',
        headerName: 'Balance',
        flex: 1,
        cellClass: 'ag-cell-right'
      }),
      createPlainColumn({
        field: 'status',
        headerName: 'Status',
        flex: 1,
        cellClass: 'ag-cell-center'
      })
    ];

    this.lineItemsColumnDefs = [
      createPlainColumn({
        field: 'lineNum',
        headerName: '#',
        width: 45,
        cellClass: 'ag-cell-center'
      }),
      createPlainColumn({
        field: 'description',
        headerName: 'Description',
        flex: 1,
        cellClass: 'ag-cell-left'
      }),
      createPlainColumn({
        field: 'qty',
        headerName: 'Qty',
        width: 70,
        cellClass: 'ag-cell-center'
      }),
      createCurrencyColumn({
        field: 'unitPrice',
        headerName: 'Unit Price',
        width: 110,
        cellClass: 'ag-cell-right'
      }),
      {
        // Replica del `<strong>` del template original: Amount en bold.
        field: 'amount',
        headerName: 'Amount',
        width: 110,
        cellClass: 'ag-cell-right',
        cellClassRules: {
          'kite-cell-bold': () => true
        },
        cellRenderer: 'currencyRenderer',
        cellRendererParams: { format: 'USD', digits: '1.2-2' }
      } as any
    ];
  }

  /** Verifica el estado de conexión QB y si el cliente ya está vinculado */
  private checkConnectionAndLink(): void {
    this.loadingStatus.set(true);
    const clientId = this.resumeData.clientID();

    // Siempre verificar el link status del cliente, independiente de la conexión OAuth
    if (clientId) {
      this.qbService.getLinkStatus(clientId).subscribe({
        next: (linkStatus) => {
          this.isLinked.set(linkStatus.linked);
          this.linkData.set(linkStatus.link || null);

          if (linkStatus.linked) {
            // Cargar el nombre QB desde el link guardado
            if (linkStatus.link?.qbDisplayName) {
              this.qbCustomerName.set(linkStatus.link.qbDisplayName);
            }
            this.loadFinancialData();
          }
        },
        error: () => { /* silencioso — el backend puede no estar listo */ }
      });
    }

    // Verificar conexión OAuth en paralelo
    this.qbService.getConnectionStatus().subscribe({
      next: (status) => {
        this.qbConnected.set(status.connected);
        this.qbRealmId.set(status.realmId || null);
        this.qbNeedsReauth.set(!!status.needsReauth);
        this.loadingStatus.set(false);
      },
      error: () => {
        this.qbConnected.set(false);
        this.loadingStatus.set(false);
      }
    });
  }

  /** Carga datos financieros desde el caché local */
  private loadFinancialData(): void {
    const clientId = this.resumeData.clientID();
    if (!clientId) return;

    this.qbService.getFinancialData(clientId).subscribe({
      next: (res: any) => {
        if (res.ok && res.data) {
          this.financialData.set(res.data);
          this.transactions.set(res.data.transactions || []);
          this.cdr.markForCheck();
        }
      },
      error: () => this.toastr.error('Error loading financial data from cache')
    });
  }

  /** Recarga el estado del link desde el backend para mantener linkData() sincronizado */
  private refreshLinkData(): void {
    const clientId = this.resumeData.clientID();
    if (!clientId) return;

    this.qbService.getLinkStatus(clientId).subscribe({
      next: (linkStatus) => {
        this.isLinked.set(linkStatus.linked);
        this.linkData.set(linkStatus.link || null);
        if (linkStatus.link?.qbDisplayName) {
          this.qbCustomerName.set(linkStatus.link.qbDisplayName);
        }
        this.cdr.markForCheck();
      }
    });
  }

  /** Abre el modal de vinculación. Si QB no está conectado, inicia OAuth primero. */
  openLinkModal(): void {
    if (!this.qbConnected()) {
      // QB no está conectado: iniciar OAuth
      this.qbService.getAuthUrl().subscribe({
        next: (res: any) => {
          if (res.ok && res.authUri) {
            // Redirigir al usuario a la página de autorización de Intuit
            window.open(res.authUri, '_blank', 'width=600,height=700');
            this.toastr.info('Complete the QuickBooks authorization in the new window, then try again.');
          }
        },
        error: () => {
          this.toastr.error('Could not start QuickBooks authorization. Verify your QB credentials in the server.');
        }
      });
      return;
    }

    // QB conectado: abrir modal de búsqueda
    this.openSearchModal();
  }

  /** Abre el modal de búsqueda de candidatos */
  private openSearchModal(): void {
    // Obtener dirección del cliente para mostrar en el modal
    const clientInfo = this.resumeData.clientInfomationTabData();
    if (clientInfo) {
      const parts = [clientInfo.address, clientInfo.city, clientInfo.state, clientInfo.zip_code].filter(Boolean);
      this.clientAddress.set(parts.join(', '));
    }

    // Abrir modal
    this.linkModalRef = this.modalService.open(this.linkModalTemplate, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    // Buscar candidatos al abrir
    this.searchCandidates();
  }

  /** Busca candidatos en QB */
  private searchCandidates(): void {
    const clientId = this.resumeData.clientID();
    const realmId = this.qbRealmId();
    if (!clientId || !realmId) return;

    this.loadingCandidates.set(true);
    this.candidates.set([]);

    this.qbService.searchCandidates(clientId, realmId).subscribe({
      next: (res: any) => {
        this.candidates.set(res.candidates || []);
        this.loadingCandidates.set(false);
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.msg || 'Error searching QuickBooks');
        this.loadingCandidates.set(false);
      }
    });
  }

  /** Vincula el cliente con el candidato seleccionado */
  linkCandidate(candidate: QbMatchCandidate): void {
    const clientId = this.resumeData.clientID();
    const realmId = this.qbRealmId();
    if (!clientId || !realmId) return;

    this.qbService.createLink(clientId, candidate.qbCustomerId, realmId, candidate.qbDisplayName).subscribe({
      next: (res: any) => {
        if (res.ok) {
          this.isLinked.set(true);
          this.qbCustomerName.set(candidate.qbDisplayName);
          this.linkModalRef?.close();
          this.toastr.success(`Linked to "${candidate.qbDisplayName}" successfully`);
          this.refreshLinkData(); // Cargar linkData completo (QB ID, fechas)
          this.syncData(); // Sync inicial
        }
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.msg || 'Error linking customer');
      }
    });
  }

  /** Cierra el modal */
  closeModal(): void {
    this.linkModalRef?.dismiss();
  }

  /** Sincroniza datos financieros desde QB */
  syncData(): void {
    const clientId = this.resumeData.clientID();
    if (!clientId) return;

    this.loadingSyncData.set(true);

    this.qbService.syncFinancialData(clientId).subscribe({
      next: (res: any) => {
        if (res.ok && res.data) {
          this.financialData.set({
            balance: res.data.summary.balance,
            totalBilled: res.data.summary.totalBilled,
            totalPaid: res.data.summary.totalPaid,
            transactions: res.data.transactions,
            lastUpdated: res.data.lastUpdated
          });
          this.transactions.set(res.data.transactions || []);
          if (res.data.qbDisplayName) {
            this.qbCustomerName.set(res.data.qbDisplayName);
          }
          this.cdr.markForCheck();
          this.toastr.success('Financial data updated from QuickBooks');
          this.qbDataChanged.emit(); // Notify parent to refresh header
          this.refreshLinkData(); // Update lastSyncDate in linkData
        }
        this.loadingSyncData.set(false);
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.msg || 'Error syncing with QuickBooks');
        this.loadingSyncData.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  /** Elimina el vínculo entre el cliente y QB */
  unlinkCustomer(): void {
    const clientId = this.resumeData.clientID();
    if (!clientId) return;

    this.modalsService.openConfirmationModal({
      title: 'Disconnect Customer',
      msg: 'Are you sure you want to disconnect this customer from QuickBooks? WARNING: All cached financial data and connection details will be permanently deleted.',
      onConfirm: () => {
        this.loadingUnlink.set(true);
        this.qbService.deleteLink(clientId).subscribe({
          next: () => {
            this.isLinked.set(false);
            this.financialData.set(null);
            this.transactions.set([]);
            this.linkData.set(null);
            this.qbCustomerName.set('');
            this.loadingUnlink.set(false);
            this.toastr.success('Customer disconnected from QuickBooks');
            this.qbDataChanged.emit(); // Refresh header card
          },
          error: (err: any) => {
            this.toastr.error(err?.error?.msg || 'Error disconnecting customer');
          }
        });
      }
    });
  }

  /** Calcula el color del badge según el score */
  getScoreColor(score: number): string {
    if (score >= 0.85) return '#28a745';
    if (score >= 0.70) return '#ffc107';
    if (score >= 0.50) return '#fd7e14';
    return '#dc3545';
  }

  getScoreLabel(score: number): string {
    if (score >= 0.85) return 'Excellent';
    if (score >= 0.70) return 'Good';
    if (score >= 0.50) return 'Fair';
    return 'Low';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  }

  getTransactionTypeColor(type: string): string {
    const colors: Record<string, string> = {
      'Invoice': '#4a81d4',
      'Payment': '#28a745',
      'CreditMemo': '#ffc107',
      'SalesReceipt': '#17a2b8'
    };
    return colors[type] || '#6c757d';
  }

  /** Replaces the legacy `onGridInit(grid)` + `grid.addEventListener(hostElement, 'click', ...)`
   *  handler. AG Grid's (cellClicked) output fires for any cell click; the original
   *  global click listener opened the detail modal from any row cell, so this
   *  preserves that behavior. */
  onTxnGridCellClicked(event: any): void {
    const item = event?.data;
    if (!item) return;
    this.openDocDetail(item);
  }

  /** Abre el modal de detalle de un documento QB — usa detalle del caché local */
  openDocDetail(txn: any): void {
    // Si el detalle ya está en el item (traído durante el sync), lo usamos directamente
    if (txn.detail) {
      this.selectedDoc.set(txn.detail as QbDocumentDetail);
      this.loadingDocDetail.set(false);
      this.docDetailModalRef = this.modalService.open(this.docDetailModalTemplate, {
        size: 'lg',
        centered: true,
        backdrop: 'static'
      });
      this.docDetailModalRef.result.finally(() => {
        this.selectedDoc.set(null);
      });
      return;
    }

    // Fallback: el sync antiguo no tenía detalle → intentar traerlo de QB
    const realmId = this.linkData()?.realmId;
    if (!realmId) {
      this.toastr.warning('No QuickBooks connection found. Please sync data first.');
      return;
    }

    this.selectedDoc.set(null);
    this.loadingDocDetail.set(true);

    this.docDetailModalRef = this.modalService.open(this.docDetailModalTemplate, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });
    this.docDetailModalRef.result.finally(() => {
      this.selectedDoc.set(null);
    });

    this.qbService.getTransactionDetail(txn.type, txn.id, realmId).subscribe({
      next: (res: any) => {
        this.selectedDoc.set(res.detail as QbDocumentDetail);
        this.loadingDocDetail.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingDocDetail.set(false);
        this.toastr.warning('Detail not available. Click "Update Data" to sync details from QuickBooks.');
        this.docDetailModalRef?.close();
      }
    });
  }

  /** Cierra el modal de detalle */
  closeDocModal(): void {
    this.docDetailModalRef?.close();
    this.selectedDoc.set(null);
  }
}
